import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, ArrowsClockwise, ChatCircleText } from "@phosphor-icons/react";
import { http } from "@/lib/api";

const feedbackApi = {
  list: () => http.get("/feedback").then((r) => r.data),
  summary: () => http.get("/feedback/summary").then((r) => r.data),
};

export default function FeedbackPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ up: 0, down: 0, total: 0, positive_pct: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([feedbackApi.list(), feedbackApi.summary()]);
      setRows(r);
      setSummary(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Customer Voice</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">POD Feedback</h1>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2" data-testid="feedback-refresh">
          <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Total responses" value={summary.total} accent="#a1a1aa" testid="feedback-tile-total" />
        <Tile label="Positive" value={summary.up} icon={<ThumbsUp size={14} weight="fill" />} accent="#22c55e" testid="feedback-tile-up" />
        <Tile label="Negative" value={summary.down} icon={<ThumbsDown size={14} weight="fill" />} accent="#ef4444" testid="feedback-tile-down" />
        <Tile label="Positive %" value={`${summary.positive_pct}%`} accent="#ff8c00" testid="feedback-tile-pct" />
      </div>

      {/* Feed */}
      <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="feedback-list">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Recent Responses</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{rows.length} showing</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]">
            No customer feedback yet — flip the toggle in Settings → Notifications to start collecting.
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {rows.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-start gap-3" data-testid={`feedback-row-${r.id}`}>
                <div
                  className={`w-9 h-9 flex items-center justify-center shrink-0 ${
                    r.rating === "up" ? "bg-[#22c55e] text-black" : "bg-[#ef4444] text-black"
                  }`}
                  title={r.rating}
                >
                  {r.rating === "up" ? <ThumbsUp size={14} weight="fill" /> : <ThumbsDown size={14} weight="fill" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-[#ff8c00] font-mono">{r.job_number || r.job_id.slice(0, 8)}</span>
                    <span className="text-white truncate">{r.customer || "—"}</span>
                    <span className="text-[#71717a] text-[10px] font-mono">{r.customer_email}</span>
                  </div>
                  {r.comment && (
                    <div className="mt-1 text-[12px] text-[#a1a1aa] flex items-start gap-1">
                      <ChatCircleText size={11} className="text-[#71717a] shrink-0 mt-0.5" weight="bold" />
                      <span className="italic">{r.comment}</span>
                    </div>
                  )}
                  <div className="text-[10px] text-[#71717a] uppercase tracking-[0.2em] mt-1">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, icon, accent, testid }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-heading font-black mt-1 flex items-center gap-2" style={{ color: accent }}>
        {icon}{value}
      </div>
    </div>
  );
}
